import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import styled from 'styled-components';

const EditorContainer = styled.div`
  .ql-editor {
    min-height: ${props => `${props.$height}px`};
    max-height: ${props => `${Math.max(props.$height + 80, props.$height)}px`};
    font-size: 14px;
    line-height: 1.5;
    overflow-y: auto;
  }
  
  .ql-toolbar {
    border-top: 1px solid #e2e8f0;
    border-left: 1px solid #e2e8f0;
    border-right: 1px solid #e2e8f0;
    border-radius: 8px 8px 0 0;
  }
  
  .ql-container {
    border-bottom: 1px solid #e2e8f0;
    border-left: 1px solid #e2e8f0;
    border-right: 1px solid #e2e8f0;
    border-radius: 0 0 8px 8px;
    font-size: 14px;
    height: auto;
  }
  
  .ql-editor.ql-blank::before {
    color: #9ca3af;
    font-style: normal;
  }
`;

const TOOLBAR_OPTIONS = [
  [{ 'header': [1, 2, 3, false] }],
  ['bold', 'italic', 'underline', 'strike'],
  [{ 'list': 'ordered'}, { 'list': 'bullet' }],
  [{ 'indent': '-1'}, { 'indent': '+1' }],
  ['blockquote', 'code-block'],
  ['link', 'image'],
  [{ 'color': [] }, { 'background': [] }],
  [{ 'align': [] }],
  ['clean']
];

const FORMATS = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'list', 'bullet', 'indent', 'blockquote', 'code-block',
  'link', 'image', 'color', 'background', 'align'
];

const RichTextEditor = ({
  value,
  onChange,
  placeholder = "Enter description...",
  height = 120,
  onImageUpload,
  disabled = false,
}) => {
  const quillRef = useRef(null);
  const onImageUploadRef = useRef(onImageUpload);
  const hasImageUpload = typeof onImageUpload === 'function';

  useEffect(() => {
    onImageUploadRef.current = onImageUpload;
  }, [onImageUpload]);

  const handleImageUpload = useCallback(() => {
    const uploadImage = onImageUploadRef.current;

    if (typeof uploadImage !== 'function') {
      return;
    }

    const fileInput = document.createElement('input');
    fileInput.setAttribute('type', 'file');
    fileInput.setAttribute('accept', 'image/png,image/jpeg,image/webp,image/gif');

    fileInput.onchange = async () => {
      const selectedFile = fileInput.files?.[0];

      if (!selectedFile) {
        return;
      }

      try {
        const imageUrl = await uploadImage(selectedFile);

        if (!imageUrl) {
          return;
        }

        const editor = quillRef.current?.getEditor?.();

        if (!editor) {
          return;
        }

        const selection = editor.getSelection(true);
        const insertionIndex = selection?.index ?? editor.getLength();

        editor.insertEmbed(insertionIndex, 'image', imageUrl, 'user');
        editor.setSelection(insertionIndex + 1, 0, 'silent');
      } catch (error) {
        console.error('Failed to upload editor image:', error);
        window.alert('Failed to upload image. Please try again.');
      }
    };

    fileInput.click();
  }, []);

  const modules = useMemo(() => ({
    toolbar: {
      container: TOOLBAR_OPTIONS,
      ...(hasImageUpload
        ? {
            handlers: {
              image: handleImageUpload,
            },
          }
        : {}),
    },
  }), [handleImageUpload, hasImageUpload]);

  return (
    <EditorContainer $height={height}>
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value}
        onChange={onChange}
        modules={modules}
        formats={FORMATS}
        placeholder={placeholder}
        readOnly={disabled}
      />
    </EditorContainer>
  );
};

export default RichTextEditor;
